import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper, Button, Table, TableHead, TableBody, TableRow, TableCell, Checkbox } from '@mui/material';
import TanStackTable from '../components/TanStackTable';
import { OrgContext } from '../contexts/OrgContext'; // Import OrgContext
import { getProductsByOrgId, deleteMultipleProducts } from '../services/productService'; // Import product service
import Papa from 'papaparse'; // Import Papa for CSV export
import BulkActionResults from '../components/BulkActionResults';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [deleteResults, setDeleteResults] = useState(null);
  const navigate = useNavigate();
  const { orgId } = useContext(OrgContext); // Access orgId from OrgContext
  
  // Pagination and filtering state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('product_name');
  const [sortOrder, setSortOrder] = useState('ASC');

  const fetchProducts = React.useCallback(async (options = {}) => {
    setLoading(true);
    try {
      // Ensure search is passed correctly, even if it's a number like 0
      const searchValue = options.search !== undefined ? options.search : searchQuery;
      
      const response = await getProductsByOrgId(orgId, {
        page: options.page || pagination.page,
        limit: options.limit || pagination.limit,
        search: searchValue, // Use searchValue which properly handles all values including 0
        sortBy: options.sortBy || sortBy,
        sortOrder: options.sortOrder || sortOrder
      });
      
      // Check if response is paginated
      if (response.data && Array.isArray(response.data)) {
        setProducts(response.data);
        setPagination(response.pagination);
      } else if (Array.isArray(response)) {
        // Fallback for non-paginated response
        setProducts(response);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [orgId, pagination.page, pagination.limit, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    if (orgId) {
      fetchProducts();
    }
  }, [orgId]);

  const handleDownload = () => {
    const filteredProducts = products.map(({ meta, ...rest }) => rest); // Exclude meta field
    const csv = Papa.unparse(filteredProducts);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const now = new Date();
    const isoDate = now.toISOString().slice(0, 10); // yyyy-mm-dd format
    const uniqueId = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}${String(now.getMilliseconds()).padStart(3, '0')}`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${isoDate}_products_${uniqueId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteClick = () => {
    setDeleteMode(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await deleteMultipleProducts(selectedProducts);
      setDeleteResults(response); // Remove .data since it's already handled in the service
      await fetchProducts();
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteMode(false);
      setSelectedProducts([]);
    }
  };

  const handleCancelDelete = () => {
    setDeleteMode(false);
    setSelectedProducts([]);
    setDeleteResults(null); // Clear delete results when canceling
  };

  const handleCloseResults = () => {
    setDeleteResults(null);
  };

  const columns = [
    {
      accessorKey: 'id',
      header: 'Product ID',
    },
    {
      accessorKey: 'product_name',
      header: 'Product Name',
    },
    {
      accessorKey: 'category',
      header: 'Category',
    },
    {
      accessorKey: 'sub_category',
      header: 'Sub Category',
    },
    {
      accessorKey: 'unit_of_measure',
      header: 'Unit of Measure',
    },
    {
      accessorKey: 'company',
      header: 'Company',
    },
    {
      accessorKey: 'sku',
      header: 'SKU',
    },
  ];

  const handleRowClick = (row) => {
    if (!deleteMode) {
      navigate(`/${orgId}/products/${row.id}`);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Products
      </Typography>

      {/* Update BulkActionResults to include onClose */}
      {deleteResults && (
        <BulkActionResults 
          results={deleteResults} 
          actionType="deletion"
          onClose={handleCloseResults}
        />
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {!deleteMode && (
          <>
            <Button variant="contained" onClick={() => navigate(`/${orgId}/products/add`)}>
              Add Product
            </Button>
            <Button variant="contained" onClick={() => navigate(`/${orgId}/products/bulk-import`)}>
              Add Products in Bulk
            </Button>
            <Button variant="contained" onClick={handleDownload}>
              Download Product List
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteClick}>
              Delete Products
            </Button>
          </>
        )}
        {deleteMode && (
          <>
            <Button variant="contained" color="error" onClick={handleConfirmDelete}>
              Confirm Delete
            </Button>
            <Button variant="contained" onClick={handleCancelDelete}>
              Cancel
            </Button>
          </>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!deleteMode && (
            <TanStackTable
              data={products}
              columns={columns}
              onRowClick={handleRowClick}
              enablePagination={true}
              pagination={pagination}
              onPaginationChange={(newPage, newLimit) => {
                console.log('Products pagination change requested:', newPage, newLimit);
                setPagination(prev => ({...prev, page: newPage, limit: newLimit}));
                fetchProducts({page: newPage, limit: newLimit});
              }}
              searchQuery={searchQuery}
              onSearchChange={(search) => {
                // Ensure search query state is set correctly
                setSearchQuery(search);
                setPagination(prev => ({...prev, page: 1}));
                // Explicitly pass search to fetchProducts
                fetchProducts({search, page: 1});
              }}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={(sortBy, sortOrder) => {
                setSortBy(sortBy);
                setSortOrder(sortOrder);
                fetchProducts({sortBy, sortOrder});
              }}
            />
          )}
          {deleteMode && (
            <Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Select</TableCell>
                    <TableCell>Product ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Sub Category</TableCell>
                    <TableCell>Unit of Measure</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>SKU</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => {
                            setSelectedProducts((prev) =>
                              prev.includes(product.id)
                                ? prev.filter((id) => id !== product.id)
                                : [...prev, product.id]
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell>{product.id}</TableCell>
                      <TableCell>{product.product_name}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell>{product.sub_category}</TableCell>
                      <TableCell>{product.unit_of_measure}</TableCell>
                      <TableCell>{product.company}</TableCell>
                      <TableCell>{product.sku}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default ProductList;
